﻿import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Calculate as CalcIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Edit as EditIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FileUpload as ExportIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Sync as SyncIcon,
  Rule as RuleIcon,
} from "@mui/icons-material";
import { FbaArticlesApi } from "../../../../services/api/multi/fba";
import taskStatusService, {
  normalizeTaskKey,
} from "../../../../services/ws/taskStatus";
import {
  RepricerArticlesApi,
  OttoRepricerApi,
} from "../../../../services/api/multi/repricer";
import { formatPricingHistory } from "../../utils/pricingHistory";
import { useTheme } from "@mui/material/styles";
import { getIgnoredRowSx } from "../../../../theme/panelStyles";
import {
  ArticleSalesStatsDialog,
  SalesStatsOpenButton,
} from "../../components/ArticleSalesStatsDialog";

// ── helpers ──────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  active: "success",
  update_needed: "warning",
  error: "error",
  oos: "default",
  paused: "info",
  unknown: "default",
};

const fmt = (val, decimals = 2) =>
  val != null ? `${Number(val).toFixed(decimals)} €` : null;

const fmtCurr = (val, currency = "EUR", decimals = 2) => {
  if (val == null) return null;
  const map = { EUR: "€", SEK: "kr", PLN: "zł" };
  const sym = map[currency] || currency;
  return `${Number(val).toFixed(decimals)} ${sym}`;
};

const DetailRow = ({ label, value }) => (
  <Grid container sx={{ py: 0.4 }} alignItems="baseline">
    <Grid item xs={5}>
      <Typography variant="caption" color="text.secondary">
        {label}:
      </Typography>
    </Grid>
    <Grid item xs={7}>
      <Typography variant="caption" fontWeight={500}>
        {value ?? "—"}
      </Typography>
    </Grid>
  </Grid>
);

function ArticleExpandedDetails({ article, onNavigateToBase }) {
  const sd = article.fba;
  return (
    <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Typography
            variant="subtitle2"
            color="primary"
            gutterBottom
            onClick={() => onNavigateToBase && onNavigateToBase(article.sku)}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            Base
          </Typography>
          <Divider sx={{ mb: 0.5 }} />
          <DetailRow label="Name" value={article.articlename} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="EANs" value={article.eans?.join(", ")} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow label="Buy Price" value={fmt(article.brutto_buy_price)} />

          <Divider sx={{ my: 0.5, borderStyle: "dotted" }} />

          <DetailRow
            label="Last Inbound Date"
            value={article.last_inbound_date}
          />
          <DetailRow label="Article Type" value={article.article_type} />
          <DetailRow
            label="Pricing History"
            value={formatPricingHistory(article.pricing_history)}
          />
          <DetailRow
            label="Ratios (Last 10)"
            value={
              article.ratios?.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {article.ratios.slice(0, 10).map((r, i) => (
                    <Chip
                      key={i}
                      label={typeof r === "number" ? r.toFixed(2) : r}
                      size="small"
                      color="default"
                      variant="outlined"
                      sx={{ height: 18, fontSize: "0.6rem" }}
                    />
                  ))}
                </Stack>
              ) : null
            }
          />
        </Grid>
        {sd && (
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Fba Config
            </Typography>
            <Divider sx={{ mb: 0.5 }} />
            <DetailRow label="Margin Rule ID" value={sd.margin_rule_id} />
            <DetailRow label="Seller SKU" value={sd.seller_sku} />
            <DetailRow label="ASIN" value={sd.asin} />
            <DetailRow
              label="Dimensions"
              value={
                sd.length != null
                  ? `${sd.length} x ${sd.width} x ${sd.height} ${
                      sd.dim_unit || ""
                    }`
                  : null
              }
            />
            <DetailRow
              label="Weight"
              value={
                sd.weight != null
                  ? `${sd.weight} ${sd.weight_unit || ""}`
                  : null
              }
            />
            <DetailRow
              label="Use Min/Max Rules"
              value={
                sd.use_min_max_rules != null
                  ? sd.use_min_max_rules
                    ? "Yes"
                    : "No"
                  : null
              }
            />
            <DetailRow
              label="Last Updated"
              value={
                sd.updated_at ? new Date(sd.updated_at).toLocaleString() : null
              }
            />
            {sd.product_url && (
              <DetailRow
                label="Product URL"
                value={
                  <a
                    href={sd.product_url}
                    target="_blank"
                    rel="noreferrer"
                    style={{ fontSize: "0.75rem" }}
                  >
                    Open
                  </a>
                }
              />
            )}
            {sd.error_message && (
              <DetailRow
                label="Error"
                value={
                  <Typography variant="caption" color="error">
                    {sd.error_message}
                  </Typography>
                }
              />
            )}
          </Grid>
        )}

        {sd?.international_pricing &&
          Object.keys(sd.international_pricing).length > 0 && (
            <Grid item xs={12} md={6}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                International Pricing
              </Typography>
              <Divider sx={{ mb: 0.5 }} />
              <InternationalPricingTable pricing={sd.international_pricing} />
            </Grid>
          )}
      </Grid>
    </Box>
  );
}

function InternationalPricingTable({ pricing }) {
  const theme = useTheme();
  const [expandedCountries, setExpandedCountries] = useState(new Set());

  if (!pricing || Object.keys(pricing).length === 0) return null;

  const toggleRow = (country) => {
    setExpandedCountries((prev) => {
      const next = new Set(prev);
      if (next.has(country)) next.delete(country);
      else next.add(country);
      return next;
    });
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.100" }}>
            <TableCell padding="checkbox" />
            <TableCell sx={{ py: 0.5 }}>Country</TableCell>
            <TableCell sx={{ py: 0.5 }}>FBA Shipping</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>
              Min Price
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>
              Max Price
            </TableCell>
            <TableCell sx={{ py: 0.5 }}>Ignored</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.entries(pricing).map(([country, data]) => {
            const expanded = expandedCountries.has(country);
            if (!data) return null;
            if (data.error) {
              return (
                <TableRow key={country}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ py: 0.5 }}>{country}</TableCell>
                  <TableCell colSpan={4} sx={{ py: 0.5 }}>
                    <Typography color="error" variant="caption">
                      Error: {data.error}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            }
            const curr = data.currency || "EUR";
            const isIgnored = !!data.ignore;
            return (
              <React.Fragment key={country}>
                <TableRow
                  hover
                  sx={{
                    cursor: "pointer",
                    ...getIgnoredRowSx(theme, isIgnored),
                    "& > td": expanded ? { borderBottom: "none" } : {},
                  }}
                  onClick={() => toggleRow(country)}
                >
                  <TableCell padding="checkbox" sx={{ py: 0.5 }}>
                    <IconButton size="small">
                      {expanded ? (
                        <CollapseIcon fontSize="small" />
                      ) : (
                        <ExpandIcon fontSize="small" />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>{country}</TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {fmtCurr(
                        data.fba_fulfillment_fee ?? data.fba_shipping_cost,
                        curr,
                      )}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {fmtCurr(data.min_bound?.target_price, curr)} (Profit:{" "}
                      {fmtCurr(data.min_bound?.profit, curr)})
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={{ py: 0.5 }}>
                    <Typography variant="caption">
                      {fmtCurr(data.max_bound?.target_price, curr)} (Profit:{" "}
                      {fmtCurr(data.max_bound?.profit, curr)})
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.5 }}>
                    <Typography
                      variant="caption"
                      color={isIgnored ? "error" : "success"}
                    >
                      {isIgnored ? "True" : "False"}
                    </Typography>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={6}
                    sx={{ p: 0, border: expanded ? undefined : "none" }}
                  >
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, py: 1.5, bgcolor: "grey.50" }}>
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                              display="block"
                            >
                              General
                            </Typography>
                            <DetailRow
                              label="Buy Netto"
                              value={fmtCurr(data.buy_netto, curr)}
                            />
                            <DetailRow
                              label="VAT Rate"
                              value={
                                data.vat_rate != null
                                  ? `${(data.vat_rate * 100).toFixed(2)}%`
                                  : data.tax_amount != null
                                    ? `${data.tax_amount}%`
                                    : "—"
                              }
                            />
                            <DetailRow
                              label="Hazmat"
                              value={
                                data.hazmat != null
                                  ? data.hazmat === 1
                                    ? "Yes"
                                    : "No"
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="FBA Shipping Cost"
                              value={fmtCurr(
                                data.fba_fulfillment_fee ??
                                  data.fba_shipping_cost,
                                curr,
                              )}
                            />
                            <DetailRow
                              label="Other Fee"
                              value={fmtCurr(data.other_fee, curr)}
                            />
                            <DetailRow
                              label="Storage Fee"
                              value={fmtCurr(data.storage_fee, curr)}
                            />

                            <DetailRow
                              label="Non-Pan EU Fee"
                              value={fmtCurr(
                                data.fulfilment_non_pan_eu_fee,
                                curr,
                              )}
                            />
                            <DetailRow
                              label="Ignored"
                              value={isIgnored ? "True" : "False"}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                              display="block"
                            >
                              Min Bound Details
                            </Typography>
                            <DetailRow
                              label="Margin"
                              value={
                                data.min_bound?.margin_pct != null
                                  ? `${data.min_bound.margin_pct.toFixed(2)}%`
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="Referral Fee (%)"
                              value={
                                data.min_bound?.referral_fee_pct != null
                                  ? `${data.min_bound.referral_fee_pct.toFixed(
                                      2,
                                    )}%`
                                  : data.order_fee != null
                                    ? `${data.order_fee}%`
                                    : "—"
                              }
                            />
                            <DetailRow
                              label="Referral Fee (Abs)"
                              value={fmtCurr(
                                data.min_bound?.referral_fee_abs,
                                curr,
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} sm={4}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              gutterBottom
                              display="block"
                            >
                              Max Bound Details
                            </Typography>
                            <DetailRow
                              label="Margin"
                              value={
                                data.max_bound?.margin_pct != null
                                  ? `${data.max_bound.margin_pct.toFixed(2)}%`
                                  : "—"
                              }
                            />
                            <DetailRow
                              label="Referral Fee (%)"
                              value={
                                data.max_bound?.referral_fee_pct != null
                                  ? `${data.max_bound.referral_fee_pct.toFixed(
                                      2,
                                    )}%`
                                  : data.order_fee != null
                                    ? `${data.order_fee}%`
                                    : "—"
                              }
                            />
                            <DetailRow
                              label="Referral Fee (Abs)"
                              value={fmtCurr(
                                data.max_bound?.referral_fee_abs,
                                curr,
                              )}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Edit Fba details dialog ───────────────────────────────────────────────

const EMPTY_SD_FORM = {
  status: "active",
  margin_rule_id: "",
  use_min_max_rules: true,
};

const ARTICLE_STATUS_OPTIONS = [
  "active",
  "paused",
  "error",
  "update_needed",
  "oos",
  "unknown",
];

function EditFbaDetailsDialog({ article, open, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY_SD_FORM);
  const [countryPrices, setCountryPrices] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!article?.fba) return;
    const sd = article.fba;
    setForm({
      status: sd.status || "active",
      margin_rule_id: sd.margin_rule_id ?? "",
      use_min_max_rules: sd.use_min_max_rules ?? true,
    });

    const prices = {};
    if (sd.international_pricing) {
      Object.entries(sd.international_pricing).forEach(([country, details]) => {
        if (details && !details.error) {
          prices[country] = {
            min: details.min_bound?.target_price ?? "",
            max: details.max_bound?.target_price ?? "",
          };
        }
      });
    }
    setCountryPrices(prices);
    setError(null);
  }, [article]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const sd = article.fba;
      const payload = {};
      if ((form.status || "active") !== (sd.status || "active"))
        payload.status = form.status || "active";
      if (String(form.margin_rule_id) !== String(sd.margin_rule_id ?? ""))
        payload.margin_rule_id =
          form.margin_rule_id !== "" ? Number(form.margin_rule_id) : null;
      if (form.use_min_max_rules !== (sd.use_min_max_rules ?? true))
        payload.use_min_max_rules = form.use_min_max_rules;

      let pricesChanged = false;
      const newIntlPricing = JSON.parse(
        JSON.stringify(sd.international_pricing || {}),
      );

      Object.entries(countryPrices).forEach(([country, bounds]) => {
        const originalMin = newIntlPricing[country]?.min_bound?.target_price;
        const originalMax = newIntlPricing[country]?.max_bound?.target_price;

        // Always ensure the ignore flag is included
        const ignore =
          article?.fba?.international_pricing?.[country]?.ignore ?? false;
        if (!newIntlPricing[country])
          newIntlPricing[country] = { min_bound: {}, max_bound: {} };
        if (!newIntlPricing[country].min_bound)
          newIntlPricing[country].min_bound = {};
        if (!newIntlPricing[country].max_bound)
          newIntlPricing[country].max_bound = {};
        newIntlPricing[country].ignore = ignore;

        if (
          String(bounds.min) !== String(originalMin ?? "") ||
          String(bounds.max) !== String(originalMax ?? "")
        ) {
          pricesChanged = true;
          newIntlPricing[country].min_bound.target_price =
            bounds.min !== "" ? Number(bounds.min) : null;
          newIntlPricing[country].max_bound.target_price =
            bounds.max !== "" ? Number(bounds.max) : null;
        }
      });

      payload.international_pricing = newIntlPricing;

      await RepricerArticlesApi.updateFbaDetails(article.sku, payload);
      onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit FBA Details: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={form.status || "active"}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                {ARTICLE_STATUS_OPTIONS.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Margin Rule ID"
              value={form.margin_rule_id}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, margin_rule_id: e.target.value }))
              }
              type="number"
              size="small"
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.use_min_max_rules}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      use_min_max_rules: e.target.checked,
                    }))
                  }
                />
              }
              label="Autocalculate Rules (Use Margin Rule Bounds)"
            />
            {(article?.article_type === "penner" ||
              article?.article_type === "renner") && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ ml: 6, mt: -0.5 }}
              >
                Article type is {article.article_type}. Enabling rules opts this
                FBA channel into margin-rule bounds instead of wave prices.
              </Typography>
            )}
          </Grid>

          {Object.keys(countryPrices).length > 0 && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                International Bounds
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {Object.entries(countryPrices).map(([country, bounds]) => {
                // Find the ignore flag from the original article.fba.international_pricing if available
                const ignore =
                  article?.fba?.international_pricing?.[country]?.ignore ??
                  false;
                return (
                  <Grid
                    container
                    spacing={2}
                    key={country}
                    sx={{ mb: 1 }}
                    alignItems="center"
                  >
                    <Grid item xs={2}>
                      <Typography variant="body2">{country}</Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Min Price"
                        value={bounds.min}
                        onChange={(e) =>
                          setCountryPrices((prev) => ({
                            ...prev,
                            [country]: {
                              ...prev[country],
                              min: e.target.value,
                            },
                          }))
                        }
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ step: "any" }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Max Price"
                        value={bounds.max}
                        onChange={(e) =>
                          setCountryPrices((prev) => ({
                            ...prev,
                            [country]: {
                              ...prev[country],
                              max: e.target.value,
                            },
                          }))
                        }
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ step: "any" }}
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={ignore}
                            onChange={(e) => {
                              // Update ignore flag in the original article.fba.international_pricing
                              const newIgnore = e.target.checked;
                              // Deep clone to avoid mutation
                              const newIntlPricing = JSON.parse(
                                JSON.stringify(
                                  article.fba.international_pricing || {},
                                ),
                              );
                              if (!newIntlPricing[country])
                                newIntlPricing[country] = {
                                  min_bound: {},
                                  max_bound: {},
                                };
                              newIntlPricing[country].ignore = newIgnore;
                              // Update the article.fba.international_pricing reference
                              // Also update countryPrices to trigger rerender
                              setCountryPrices((prev) => ({ ...prev }));
                              // Save the change for payload
                              article.fba.international_pricing =
                                newIntlPricing;
                            }}
                            color="primary"
                          />
                        }
                        label="Ignore"
                        labelPlacement="end"
                      />
                    </Grid>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Delete Fba details dialog ─────────────────────────────────────────────

function DeleteFbaDetailsDialog({ sku, open, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await RepricerArticlesApi.deleteFbaDetails(sku);
      onDeleted();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Remove Fba Details</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}
        <Typography>
          Remove Fba details for <strong>{sku}</strong>? The base article will
          remain.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function StatusChip({ status }) {
  return (
    <Chip
      label={status || "—"}
      color={STATUS_COLORS[status] || "default"}
      size="small"
      variant="outlined"
    />
  );
}

function StatsBar({ stats }) {
  if (!stats) return null;
  const items = [
    { label: "Total", value: stats.total_count, color: "primary" },
    { label: "Stock", value: stats.total_stock, color: "success" },
    ...(stats.by_status
      ? Object.entries(stats.by_status).map(([s, v]) => ({
          label: s,
          value: v.count,
          color: STATUS_COLORS[s] || "default",
        }))
      : []),
  ];
  return (
    <Stack
      direction="row"
      spacing={1}
      flexWrap="wrap"
      useFlexGap
      sx={{ mb: 2 }}
    >
      {items.map((i) => (
        <Chip
          key={i.label}
          label={`${i.label}: ${i.value}`}
          color={i.color}
          size="small"
        />
      ))}
    </Stack>
  );
}

// ── Margin rules dialog ────────────────────────────────────────────────────────

function MarginRuleDialog({ article, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [rule, setRule] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !article) {
      setRule(null);
      setError(null);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    const fetchRule = async () => {
      try {
        const id = article.fba?.margin_rule_id;
        if (id != null) {
          const res = await OttoRepricerApi.getMarginRule(id);
          if (active) setRule(res.data);
        } else {
          if (active) setRule(null);
        }
      } catch (err) {
        if (active) setError(err.response?.data?.detail || err.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchRule();

    return () => {
      active = false;
    };
  }, [open, article]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>FBA Margin Rule: {article?.sku}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box>
            <Typography
              variant="subtitle2"
              sx={{ color: "primary.main", mb: 1 }}
            >
              Margin Rule ID: {article?.fba?.margin_rule_id || "None"}
            </Typography>
            {!rule ? (
              <Typography variant="caption" color="text.secondary">
                Not found or not configured
              </Typography>
            ) : (
              <Box sx={{ p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">
                      Buy Price (up to):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.buy_price)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.min_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Max Margin:
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {rule.max_margin} %
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Profit (Min Price):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.min_min_profit)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="text.secondary">
                      Min Profit (Max Price):
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {fmt(rule.max_min_profit)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function FbaArticles({
  initialSku = null,
  onSkuConsumed,
  onNavigateToBase,
} = {}) {
  const [articles, setArticles] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [salesStatsSku, setSalesStatsSku] = useState(null);

  // search / filter
  const [skuSearch, setSkuSearch] = useState(initialSku || "");
  const [activeSkuSearch, setActiveSkuSearch] = useState(initialSku || "");
  const [sellerSkuSearch, setSellerSkuSearch] = useState("");
  const [activeSellerSkuSearch, setActiveSellerSkuSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  // advanced filters & sort
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [activeAdvancedFilters, setActiveAdvancedFilters] = useState([]);
  const [sortOrder, setSortOrder] = useState({ field: null, dir: null });

  const [searchTrigger, setSearchTrigger] = useState(0);

  // consume initialSku navigation
  useEffect(() => {
    if (initialSku) {
      setSkuSearch(initialSku);
      setActiveSkuSearch(initialSku);
      setPage(0);
      if (onSkuConsumed) onSkuConsumed();
    }
  }, [initialSku, onSkuConsumed]);

  // pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);

  // expanded rows
  const [expandedRows, setExpandedRows] = useState(new Set());

  // edit/delete details dialogs
  const [editDetailsArticle, setEditDetailsArticle] = useState(null);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  const [deleteDetailsSku, setDeleteDetailsSku] = useState(null);
  const [deleteDetailsOpen, setDeleteDetailsOpen] = useState(false);

  // margin rules dialog
  const [marginRuleArticle, setMarginRuleArticle] = useState(null);
  const [marginRuleOpen, setMarginRuleOpen] = useState(false);

  // per-row action loading
  const [recalcLoading, setRecalcLoading] = useState({});
  const [exportLoading, setExportLoading] = useState({});
  const [syncLoading, setSyncLoading] = useState({});
  const [exportingAll, setExportingAll] = useState(false);
  const [recalcAllLoading, setRecalcAllLoading] = useState(false);
  const [syncAllLoading, setSyncAllLoading] = useState(false);

  // sync ledger dialog
  const [syncLedgerDialog, setSyncLedgerDialog] = useState({
    open: false,
    asin: "",
    startTime: "",
    endTime: "",
    loading: false,
  });

  const toggleRow = (sku) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(sku)) next.delete(sku);
      else next.add(sku);
      return next;
    });
  };

  const buildFilters = useCallback(() => {
    // always restrict to articles with Fba details
    const f = [{ field: "fba.article_id", op: "is_not_null" }];
    if (activeSkuSearch)
      f.push({ field: "sku", op: "like", value: `%${activeSkuSearch}%` });
    if (activeSellerSkuSearch)
      f.push({
        field: "fba.seller_sku",
        op: "like",
        value: `%${activeSellerSkuSearch}%`,
      });
    if (statusFilter !== "ALL")
      f.push({ field: "fba.status", op: "eq", value: statusFilter });

    // advanced filters
    for (const af of activeAdvancedFilters) {
      if (af.field && af.op && af.value !== "") {
        f.push({ field: af.field, op: af.op, value: af.value });
      }
    }
    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeSkuSearch,
    activeSellerSkuSearch,
    statusFilter,
    searchTrigger,
    activeAdvancedFilters,
  ]);

  const loadArticles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = buildFilters();
      const orders = sortOrder.field
        ? [{ field: sortOrder.field, dir: sortOrder.dir }]
        : undefined;
      const res = await RepricerArticlesApi.getArticles({
        details: "fba",
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        filters,
        orders,
      });
      setArticles(res.data || []);
      setTotal(res.data?.length ?? 0);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [buildFilters, page, rowsPerPage, sortOrder]);

  const loadStats = useCallback(async () => {
    try {
      const res = await RepricerArticlesApi.getArticleStats("fba");
      setStats(res.data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    loadArticles();
  }, [loadArticles]);
  useEffect(() => {
    loadStats();
  }, [loadStats]);

  useEffect(() => {
    const unsubFinish = taskStatusService.subscribeFinish((msg) => {
      const source = (msg.source || "").toLowerCase();
      const task = normalizeTaskKey(msg.task || msg.name);
      if (source === "fba" && task === "recalc" && msg.success !== false) {
        loadArticles();
        loadStats();
      }
    });
    const unsubItem = taskStatusService.subscribeItemUpdate((msg) => {
      if (
        msg.state === "recalculated" &&
        (msg.detail?.source || "").toLowerCase() === "fba"
      ) {
        loadArticles();
        loadStats();
      }
    });
    return () => {
      unsubFinish();
      unsubItem();
    };
  }, [loadArticles, loadStats]);

  const handleSearch = () => {
    setPage(0);
    setActiveSkuSearch(skuSearch);
    setActiveSellerSkuSearch(sellerSkuSearch);
    setSearchTrigger((prev) => prev + 1);
    setActiveAdvancedFilters([...advancedFilters]);
  };
  const handleClearSearch = () => {
    setSkuSearch("");
    setActiveSkuSearch("");
    setSellerSkuSearch("");
    setActiveSellerSkuSearch("");
    setPage(0);
    setSearchTrigger((prev) => prev + 1);
  };
  const handleSort = (field) => {
    setSortOrder((prev) => {
      const isAsc = prev.field === field && prev.dir === "asc";
      const isDesc = prev.field === field && prev.dir === "desc";
      if (isAsc) return { field, dir: "desc" };
      if (isDesc) return { field: null, dir: null };
      return { field, dir: "asc" };
    });
    setPage(0);
  };

  const handleAddAdvancedFilter = () => {
    setAdvancedFilters((prev) => [
      ...prev,
      { id: Date.now(), field: "brutto_buy_price", op: "eq", value: "" },
    ]);
  };

  const handleRemoveAdvancedFilter = (id) => {
    setAdvancedFilters((prev) => prev.filter((f) => f.id !== id));
  };

  const handleUpdateAdvancedFilter = (id, key, val) => {
    setAdvancedFilters((prev) =>
      prev.map((f) => (f.id === id ? { ...f, [key]: val } : f)),
    );
  };

  const handleExportAll = async () => {
    setExportingAll(true);
    setError(null);
    try {
      await FbaArticlesApi.exportUpdates();
      setSuccess("Export triggered for all articles");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setExportingAll(false);
    }
  };

  const handleRecalculateAll = async () => {
    setRecalcAllLoading(true);
    setError(null);
    try {
      await FbaArticlesApi.recalculate("all");
      setSuccess("Recalculate triggered for all articles");
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRecalcAllLoading(false);
    }
  };

  const handleSyncAll = async () => {
    setSyncAllLoading(true);
    setError(null);
    try {
      await FbaArticlesApi.sync("all");
      setSuccess("Sync triggered for all articles");
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncAllLoading(false);
    }
  };

  const handleSyncLedgerSubmit = async () => {
    try {
      setSyncLedgerDialog((prev) => ({ ...prev, loading: true }));
      await FbaArticlesApi.syncLedger(
        syncLedgerDialog.asin,
        syncLedgerDialog.startTime,
        syncLedgerDialog.endTime,
      );
      setSuccess("Ledger report sync initiated successfully.");
      setSyncLedgerDialog({
        open: false,
        asin: "",
        startTime: "",
        endTime: "",
        loading: false,
      });
    } catch (e) {
      console.error("Failed to sync ledger:", e);
      setError(
        e.response?.data?.detail || e.message || "Failed to sync ledger report",
      );
      setSyncLedgerDialog((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleRecalculate = async (sku) => {
    setRecalcLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await FbaArticlesApi.recalculate(sku);
      setSuccess(`Recalculated: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setRecalcLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleExport = async (sku) => {
    setExportLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await FbaArticlesApi.exportUpdates(sku);
      setSuccess(`Exported: ${sku}`);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setExportLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  const handleSync = async (sku) => {
    setSyncLoading((prev) => ({ ...prev, [sku]: true }));
    setError(null);
    try {
      await FbaArticlesApi.sync(sku);
      setSuccess(`Synced variation SKUs for: ${sku}`);
      await loadArticles();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setSyncLoading((prev) => ({ ...prev, [sku]: false }));
    }
  };

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2 }}>
        Verify repricer calculations using the{" "}
        <a
          href="https://sellercentral.amazon.de/hz/fba/profitabilitycalculator/index?lang=de_DE"
          target="_blank"
          rel="noopener noreferrer"
          style={{ textDecoration: "underline", color: "#1976d2" }}
        >
          Amazon Profitability Calculator
        </a>
        .
      </Alert>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {typeof error === "object" ? JSON.stringify(error) : error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Stats */}
          <StatsBar stats={stats} />

          {/* Action bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <Button
              variant="outlined"
              startIcon={
                syncAllLoading ? <CircularProgress size={16} /> : <SyncIcon />
              }
              onClick={handleSyncAll}
              disabled={syncAllLoading}
              size="small"
            >
              Sync All
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() =>
                setSyncLedgerDialog((prev) => ({ ...prev, open: true }))
              }
              size="small"
            >
              Sync Ledger Report
            </Button>
            <Button
              variant="outlined"
              startIcon={
                recalcAllLoading ? <CircularProgress size={16} /> : <CalcIcon />
              }
              onClick={handleRecalculateAll}
              disabled={recalcAllLoading}
              size="small"
            >
              Recalculate All
            </Button>

            <Button
              variant="outlined"
              startIcon={
                exportingAll ? <CircularProgress size={16} /> : <ExportIcon />
              }
              onClick={handleExportAll}
              disabled={exportingAll}
              size="small"
            >
              Export All
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                loadArticles();
                loadStats();
              }}
              size="small"
            >
              Refresh
            </Button>
          </Stack>

          {/* Filter bar */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              placeholder="Search SKU…"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: skuSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSkuSearch("");
                      setActiveSkuSearch("");
                      setSearchTrigger((p) => p + 1);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon fontSize="small" />
                ),
              }}
              sx={{ width: 200 }}
            />
            <TextField
              size="small"
              placeholder="Search Seller SKU…"
              value={sellerSkuSearch}
              onChange={(e) => setSellerSkuSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              InputProps={{
                endAdornment: sellerSkuSearch ? (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSellerSkuSearch("");
                      setActiveSellerSkuSearch("");
                      setSearchTrigger((p) => p + 1);
                    }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ) : (
                  <SearchIcon fontSize="small" />
                ),
              }}
              sx={{ width: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="ALL">All</MenuItem>
                {["ACTIVE", "UPDATE_NEEDED", "ERROR", "OOS", "PAUSED"].map(
                  (s) => (
                    <MenuItem key={s} value={s}>
                      {s}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              startIcon={<SearchIcon />}
            >
              Search
            </Button>

            <Button
              variant={advancedFiltersOpen ? "contained" : "outlined"}
              size="small"
              onClick={() => setAdvancedFiltersOpen((prev) => !prev)}
              startIcon={<FilterListIcon />}
            >
              Filters ({advancedFilters.length})
            </Button>

            {(activeSkuSearch ||
              activeSellerSkuSearch ||
              statusFilter !== "ALL" ||
              activeAdvancedFilters.length > 0) && (
              <Button
                variant="text"
                size="small"
                onClick={handleClearSearch}
                startIcon={<ClearIcon />}
              >
                Clear
              </Button>
            )}
          </Stack>

          {/* Advanced Filters Collapse */}
          <Collapse in={advancedFiltersOpen} unmountOnExit>
            <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: "grey.50" }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                <Typography variant="subtitle2">Advanced Filters</Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddAdvancedFilter}
                >
                  Add Rule
                </Button>
              </Stack>
              {advancedFilters.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No advanced filters active.
                </Typography>
              ) : (
                <Stack spacing={1.5}>
                  {advancedFilters.map((f) => (
                    <Stack
                      key={f.id}
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                    >
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <Select
                          value={f.field}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "field",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="brutto_buy_price">
                            Buy Price
                          </MenuItem>
                          <MenuItem value="stock">Stock</MenuItem>
                          <MenuItem value="package_type">Package Type</MenuItem>
                          <MenuItem value="weight">Weight</MenuItem>
                          <MenuItem value="fba.use_min_max_rules">
                            Use Min/Max Rules
                          </MenuItem>
                        </Select>
                      </FormControl>
                      <FormControl size="small" sx={{ minWidth: 140 }}>
                        <Select
                          value={f.op}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "op",
                              e.target.value,
                            )
                          }
                        >
                          <MenuItem value="eq">Equals (=)</MenuItem>
                          <MenuItem value="gt">Greater (&gt;)</MenuItem>
                          <MenuItem value="gte">Greater eq (&gt;=)</MenuItem>
                          <MenuItem value="lt">Less (&lt;)</MenuItem>
                          <MenuItem value="lte">Less eq (&lt;=)</MenuItem>
                          <MenuItem value="like">Contains</MenuItem>
                          <MenuItem value="is_null">Is null/none</MenuItem>
                          <MenuItem value="is_not_null">Is not null</MenuItem>
                        </Select>
                      </FormControl>
                      {f.op !== "is_null" && f.op !== "is_not_null" && (
                        <TextField
                          size="small"
                          placeholder="Value"
                          value={f.value}
                          onChange={(e) =>
                            handleUpdateAdvancedFilter(
                              f.id,
                              "value",
                              e.target.value,
                            )
                          }
                          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                          sx={{ width: 140 }}
                        />
                      )}
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleRemoveAdvancedFilter(f.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  ))}
                  <Box>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSearch}
                      sx={{ mt: 1 }}
                    >
                      Apply Filters
                    </Button>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Collapse>

          {/* Table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell padding="checkbox" />
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "sku"}
                      direction={
                        sortOrder.field === "sku" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("sku")}
                    >
                      SKU
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Seller SKU</TableCell>
                  <TableCell>ASIN</TableCell>
                  <TableCell>Marketplaces</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortOrder.field === "fba.status"}
                      direction={
                        sortOrder.field === "fba.status" ? sortOrder.dir : "asc"
                      }
                      onClick={() => handleSort("fba.status")}
                    >
                      Status
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell>Last Updated</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : articles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No articles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  articles.map((a) => {
                    const sd = a.fba;
                    const expanded = expandedRows.has(a.sku);
                    const marketplaces = sd?.international_pricing
                      ? Object.keys(sd.international_pricing)
                      : [];

                    return (
                      <React.Fragment key={a.sku}>
                        <TableRow
                          hover
                          sx={{
                            cursor: "pointer",
                            "& > td": expanded ? { borderBottom: "none" } : {},
                          }}
                          onClick={() => toggleRow(a.sku)}
                        >
                          <TableCell padding="checkbox">
                            <IconButton size="small">
                              {expanded ? (
                                <CollapseIcon fontSize="small" />
                              ) : (
                                <ExpandIcon fontSize="small" />
                              )}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            {sd?.product_url ? (
                              <Typography
                                variant="body2"
                                fontWeight={500}
                                component="a"
                                href={sd.product_url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                sx={{
                                  textDecoration: "underline",
                                  color: "primary.main",
                                }}
                              >
                                {a.sku}
                              </Typography>
                            ) : (
                              <Typography variant="body2" fontWeight={500}>
                                {a.sku}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {sd?.seller_sku ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {sd?.asin ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {marketplaces.length > 0 ? (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                              >
                                {marketplaces.map((mp) => (
                                  <Chip
                                    key={mp}
                                    label={mp}
                                    size="small"
                                    variant="outlined"
                                    sx={{ height: 20, fontSize: "0.7rem" }}
                                  />
                                ))}
                              </Stack>
                            ) : (
                              "—"
                            )}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={sd?.status} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2">
                              {a.stock ?? "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontSize: "0.72rem", whiteSpace: "nowrap" }}
                            >
                              {sd?.updated_at
                                ? new Date(sd.updated_at).toLocaleString()
                                : "—"}
                            </Typography>
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Stack
                              direction="row"
                              spacing={0.5}
                              justifyContent="center"
                            >
                              <SalesStatsOpenButton
                                onClick={() => setSalesStatsSku(a.sku)}
                              />
                              <Tooltip title="Edit details">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setEditDetailsArticle(a);
                                    setEditDetailsOpen(true);
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Recalculate">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "purple" }}
                                    onClick={() => handleRecalculate(a.sku)}
                                    disabled={!!recalcLoading[a.sku]}
                                  >
                                    {recalcLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <CalcIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Sync from Sellerlogic">
                                <span>
                                  <IconButton
                                    size="small"
                                    sx={{ color: "teal" }}
                                    onClick={() => handleSync(a.sku)}
                                    disabled={!!syncLoading[a.sku]}
                                  >
                                    {syncLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <SyncIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="Export to Sellerlogic">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleExport(a.sku)}
                                    disabled={!!exportLoading[a.sku]}
                                  >
                                    {exportLoading[a.sku] ? (
                                      <CircularProgress size={14} />
                                    ) : (
                                      <ExportIcon fontSize="small" />
                                    )}
                                  </IconButton>
                                </span>
                              </Tooltip>
                              <Tooltip title="View Margin Rules">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => {
                                    setMarginRuleArticle(a);
                                    setMarginRuleOpen(true);
                                  }}
                                >
                                  <RuleIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Remove Fba details">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    setDeleteDetailsSku(a.sku);
                                    setDeleteDetailsOpen(true);
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {/* Expanded details row */}
                        <TableRow>
                          <TableCell
                            colSpan={8}
                            sx={{ p: 0, border: expanded ? undefined : "none" }}
                          >
                            <Collapse
                              in={expanded}
                              timeout="auto"
                              unmountOnExit
                            >
                              <ArticleExpandedDetails
                                article={a}
                                onNavigateToBase={onNavigateToBase}
                              />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={stats?.total_count ?? total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(+e.target.value);
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100, 200]}
          />
        </CardContent>
      </Card>

      <EditFbaDetailsDialog
        article={editDetailsArticle}
        open={editDetailsOpen}
        onClose={() => setEditDetailsOpen(false)}
        onSaved={() => loadArticles()}
      />

      <DeleteFbaDetailsDialog
        sku={deleteDetailsSku}
        open={deleteDetailsOpen}
        onClose={() => setDeleteDetailsOpen(false)}
        onDeleted={() => {
          loadArticles();
          setSuccess(`Fba details removed: ${deleteDetailsSku}`);
        }}
      />

      <MarginRuleDialog
        article={marginRuleArticle}
        open={marginRuleOpen}
        onClose={() => setMarginRuleOpen(false)}
      />

      {/* Sync Ledger Report Dialog */}
      <Dialog
        open={syncLedgerDialog.open}
        onClose={() =>
          setSyncLedgerDialog((prev) => ({ ...prev, open: false }))
        }
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Sync Inventory Ledger Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            This will calculate and update the <b>last inbound date</b> for
            matching base articles within the specified span based on your
            warehouse transfers.
            <br />
            <br />
            For more details on ledger report events, see the{" "}
            <a
              href="https://developer-docs.amazon.com/sp-api/docs/report-type-values-fba#inventory-ledger-report---detailed-view"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#1976d2", textDecoration: "none" }}
            >
              Amazon SP-API Documentation
            </a>
            .
            <br />
            <br />
            Note: You can only access all historical movements of your inventory
            for 18 months.
          </Typography>

          <Box display="flex" flexDirection="column" gap={3} sx={{ mt: 2 }}>
            <TextField
              label="ASIN (Optional, leave blank for all)"
              placeholder="e.g. B08N5N4ZXY"
              fullWidth
              value={syncLedgerDialog.asin}
              onChange={(e) =>
                setSyncLedgerDialog((prev) => ({
                  ...prev,
                  asin: e.target.value,
                }))
              }
            />
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={syncLedgerDialog.startTime}
              onChange={(e) =>
                setSyncLedgerDialog((prev) => ({
                  ...prev,
                  startTime: e.target.value,
                }))
              }
            />
            <TextField
              label="End Date"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={syncLedgerDialog.endTime}
              onChange={(e) =>
                setSyncLedgerDialog((prev) => ({
                  ...prev,
                  endTime: e.target.value,
                }))
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setSyncLedgerDialog((prev) => ({ ...prev, open: false }))
            }
            disabled={syncLedgerDialog.loading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSyncLedgerSubmit}
            disabled={syncLedgerDialog.loading}
          >
            {syncLedgerDialog.loading ? (
              <CircularProgress size={24} />
            ) : (
              "Sync Report"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <ArticleSalesStatsDialog
        open={!!salesStatsSku}
        onClose={() => setSalesStatsSku(null)}
        sku={salesStatsSku}
        articlename={articles.find((x) => x.sku === salesStatsSku)?.articlename}
        channel="fba"
      />
    </Box>
  );
}
